import React, { useMemo, useRef, useState } from 'react';
import { Collapse } from 'react-bootstrap';
import { Check, X } from 'react-bootstrap-icons';
import CareMapprIconButton from '../../../../../../components/Button/CareMapprIconButton';
import Divider from '../../../../../../components/Divider/Divider';
import {
  IMPORTANT_PATIENT_INFORMATION,
  type MedSectionOption,
} from '../../../../constants';
import './style.css';
import CreatableSelect from 'react-select/creatable';
import { CareMapprTextarea } from '../../../../../../components/Inputs/textarea';
import CareMapprTextField from '../../../../../../components/Inputs/textField/CareMapprTextField';

interface AddSectionProps {
  open: boolean;
  onClose: () => void;
  onAddSection: (
    name: string,
    options?: { position?: 'top'; sectionText?: string; medName?: string },
  ) => void;
  options: MedSectionOption[];
  mode?: 'standard' | 'importantPatientInformation';
}

const AddSection = ({
  open,
  onClose,
  onAddSection,
  options,
  mode = 'standard',
}: AddSectionProps) => {
  const [selected, setSelected] = useState<MedSectionOption | null>(null);
  const [customLabel, setCustomLabel] = useState('');
  const [importantTitle, setImportantTitle] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);

  const effectiveOptions = useMemo<MedSectionOption[]>(
    () =>
      Array.isArray(options)
        ? options.filter(
            (o) =>
              o &&
              typeof o.value === 'string' &&
              typeof o.label === 'string' &&
              o.value.trim().length > 0 &&
              o.label.trim().length > 0,
          )
        : [],
    [options],
  );

  const isImportantPatientInformationMode =
    mode === 'importantPatientInformation';
  const needsTextInput = isImportantPatientInformationMode;
  React.useEffect(() => {
    if (!needsTextInput && customLabel) {
      setCustomLabel('');
    }
  }, [needsTextInput, customLabel]);

  const handleSaveClick = () => {
    if (isImportantPatientInformationMode) {
      const sectionText = customLabel.trim();
      const title = importantTitle.trim();
      if (!title || !sectionText) return;
      onAddSection(IMPORTANT_PATIENT_INFORMATION, {
        position: 'top',
        sectionText,
        medName: title,
      });
    } else {
      const base = selected?.value ?? '';
      if (!base) return;
      onAddSection(base);
    }
    onClose();
    setSelected(null);
    setCustomLabel('');
    setImportantTitle('');
  };

  const scrollToCenter = () => {
    if (!containerRef.current) return;
    containerRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const handleClose = () => {
    onClose();
    setSelected(null);
    setCustomLabel('');
    setImportantTitle('');
  };

  return (
    <Collapse in={open} onEntered={scrollToCenter}>
      <div ref={containerRef} className="add-section-container">
        <div className="d-flex align-items-center gap-2">
          {isImportantPatientInformationMode ? (
            <div className="mt-3 view-width-30">
              <div className="editable-label-display">
                {IMPORTANT_PATIENT_INFORMATION}
              </div>
            </div>
          ) : (
            <div className="mt-3 view-width-30">
              <div className="ellipsis form-label">New section</div>
              <CreatableSelect
                options={effectiveOptions}
                value={selected}
                onChange={(next) =>
                  setSelected((next as MedSectionOption | null) ?? null)
                }
                placeholder="Select..."
                isClearable
                createOptionPosition="first"
                formatCreateLabel={(inputValue) => `"${inputValue}"`}
                isValidNewOption={(inputValue, _, opts) => {
                  const normalized = inputValue.trim().toLowerCase();
                  if (!normalized) return false;
                  return !(opts || []).some(
                    (o: any) =>
                      String(o?.value ?? '')
                        .trim()
                        .toLowerCase() === normalized,
                  );
                }}
              />
            </div>
          )}

          {!isImportantPatientInformationMode && (
            <>
              <CareMapprIconButton
                className="mb-0 mt-auto"
                icon={<X size={18} />}
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                }}
              />
              <CareMapprIconButton
                className="mb-0 mt-auto"
                icon={<Check size={18} />}
                onClick={(e) => {
                  e.preventDefault();
                  handleSaveClick();
                }}
                disabled={
                  isImportantPatientInformationMode
                    ? !customLabel.trim()
                    : !selected?.value
                }
              />
            </>
          )}
        </div>
        {needsTextInput ? (
          <div
            className="mt-3 d-flex align-items-end gap-2"
            style={{ width: '50vw' }}
          >
            <div style={{ flex: 1 }}>
              <CareMapprTextField
                className="mb-2"
                value={importantTitle}
                onChange={(e) => setImportantTitle(e.target.value)}
                placeholder="Title"
              />
              <CareMapprTextarea
                autoResize
                placeholder="Start typing..."
                controlId="important-patient-information-section-text"
                rows={4}
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
              />
            </div>
            <CareMapprIconButton
              className="mb-0"
              icon={<X size={18} />}
              onClick={(e) => {
                e.preventDefault();
                handleClose();
              }}
            />
            <CareMapprIconButton
              className="mb-0"
              icon={<Check size={18} />}
              onClick={(e) => {
                e.preventDefault();
                handleSaveClick();
              }}
              disabled={!importantTitle.trim() || !customLabel.trim()}
            />
          </div>
        ) : null}

        <Divider />
      </div>
    </Collapse>
  );
};

export default AddSection;
